import { EditorState } from "draft-js";

export interface ICaptionUrl {
  unshortenedUrl: string;
  shortenedUrl?: string;
}

const URL_ENTITY_TYPES = ["SHORTENED_URL", "UNSHORTENED_URL"];
const MAX_LABEL_LENGTH = 40;

export const getCaptionUrls = (
  editorStates: (EditorState | undefined)[]
): ICaptionUrl[] => {
  const byUrl = new Map<string, ICaptionUrl>();

  editorStates.forEach(editorState => {
    if (!editorState) {
      return;
    }

    const contentState = editorState.getCurrentContent();
    contentState.getBlockMap().forEach(block => {
      if (!block) {
        return;
      }

      block.findEntityRanges(
        character => {
          const entityKey = character.getEntity();
          return (
            entityKey !== null &&
            URL_ENTITY_TYPES.includes(
              contentState.getEntity(entityKey).getType()
            )
          );
        },
        start => {
          const entityKey = block.getEntityAt(start);
          const { unshortenedUrl, shortenedUrl } = contentState
            .getEntity(entityKey)
            .getData();

          if (unshortenedUrl && !byUrl.has(unshortenedUrl)) {
            byUrl.set(unshortenedUrl, { unshortenedUrl, shortenedUrl });
          }
        }
      );
    });
  });

  return Array.from(byUrl.values());
};

/* Mirrors the branches in scrapeUrl: without both of these the scrape adds no attachment. */
export const canPreviewScrapedLink = (
  scrapeResult: { image?: string; thumbnail?: string; shortUrl?: string; url?: string } | undefined
): boolean =>
  !!scrapeResult &&
  !!(scrapeResult.shortUrl || scrapeResult.url) &&
  !!(scrapeResult.image || scrapeResult.thumbnail);

/* A link nobody has scraped yet is offered rather than hidden: whether it has a
   preview image is only knowable by scraping it, and scraping every caption link
   up front is the round-trip cost this feature deliberately avoids. */
export const getSelectableCaptionUrls = (
  captionUrls: ICaptionUrl[],
  scrapeResultsByUrl: { [url: string]: any }
): ICaptionUrl[] =>
  captionUrls.filter(
    captionUrl =>
      !(captionUrl.unshortenedUrl in scrapeResultsByUrl) ||
      canPreviewScrapedLink(scrapeResultsByUrl[captionUrl.unshortenedUrl])
  );

export const isPreviewedCaptionUrl = (
  captionUrl: ICaptionUrl,
  attachment: { url?: string; originalUrl?: string }
): boolean =>
  attachment.originalUrl === captionUrl.unshortenedUrl ||
  (!!attachment.url &&
    (attachment.url === captionUrl.shortenedUrl ||
      attachment.url === captionUrl.unshortenedUrl));

export const formatCaptionUrlLabel = (url: string): string => {
  const label = url.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "");
  return label.length > MAX_LABEL_LENGTH
    ? `${label.slice(0, MAX_LABEL_LENGTH - 1)}…`
    : label;
};
